/**
 * StatusBadge Component
 *
 * Classification status indicator with icon and color coding.
 * Uses Lucide React icons for each classification status.
 *
 * @see types/accounting.ts for ClassificationStatus type
 */

import clsx from 'clsx';
import {
  Clock,
  AlertCircle,
  CheckCircle,
  Sparkles,
  UserCheck,
  Zap,
  ArrowRightLeft,
  Home,
  Scale,
  type LucideIcon,
} from 'lucide-react';
import type { ClassificationStatus } from '@/types/accounting';

export interface StatusBadgeProps {
  /** Classification status to display */
  status: ClassificationStatus;
  /** Whether to show the status label */
  showLabel?: boolean;
  /** Size of the badge */
  size?: 'sm' | 'md';
}

interface StatusConfig {
  color: string;
  bgColor: string;
  textColor: string;
  Icon: LucideIcon;
  label: string;
}

/**
 * Configuration for each classification status
 */
const STATUS_CONFIG: Record<ClassificationStatus, StatusConfig> = {
  pending: {
    color: 'gray',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    Icon: Clock,
    label: 'Pending',
  },
  needs_review: {
    color: 'amber',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
    Icon: AlertCircle,
    label: 'Needs Review',
  },
  rule_matched: {
    color: 'green',
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    Icon: CheckCircle,
    label: 'Rule Matched',
  },
  llm_classified: {
    color: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    Icon: Sparkles,
    label: 'AI Classified',
  },
  llm_accepted: {
    color: 'green',
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    Icon: UserCheck,
    label: 'AI Accepted',
  },
  manually_classified: {
    color: 'green',
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    Icon: UserCheck,
    label: 'Manual',
  },
  auto_classified: {
    color: 'green',
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    Icon: Zap,
    label: 'Auto',
  },
  transfer: {
    color: 'purple',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-700',
    Icon: ArrowRightLeft,
    label: 'Transfer',
  },
  mortgage_payment: {
    color: 'indigo',
    bgColor: 'bg-indigo-100',
    textColor: 'text-indigo-700',
    Icon: Home,
    label: 'Mortgage',
  },
  reconciliation: {
    color: 'slate',
    bgColor: 'bg-slate-100',
    textColor: 'text-slate-700',
    Icon: Scale,
    label: 'Reconciliation',
  },
};

/**
 * StatusBadge renders a classification status with icon and optional label.
 *
 * Features:
 * - Color-coded icons for each status type
 * - Optional label display
 * - Two size variants (sm, md)
 *
 * @example
 * <StatusBadge status="needs_review" showLabel />
 * // Renders amber badge with alert icon and "Needs Review" label
 *
 * @example
 * <StatusBadge status="rule_matched" size="sm" />
 * // Renders small green badge with checkmark icon only
 */
export function StatusBadge({
  status,
  showLabel = false,
  size = 'md',
}: StatusBadgeProps) {
  const config = STATUS_CONFIG[status];
  const { Icon, label, bgColor, textColor } = config;

  const iconSize = size === 'sm' ? 12 : 14;

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full font-medium',
        bgColor,
        textColor,
        {
          'px-1.5 py-0.5 text-xs': size === 'sm',
          'px-2 py-0.5 text-xs': size === 'md',
        }
      )}
      data-testid="status-badge"
      data-status={status}
      aria-label={label}
      title={label}
    >
      <Icon
        size={iconSize}
        className="flex-shrink-0"
        aria-hidden="true"
      />
      {showLabel && (
        <span className="whitespace-nowrap">{label}</span>
      )}
    </span>
  );
}

/**
 * Get the status configuration for a given status
 * Useful for custom styling based on status
 */
export function getStatusConfig(status: ClassificationStatus): StatusConfig {
  return STATUS_CONFIG[status];
}

export default StatusBadge;
